import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Award, Play, Loader2, Download, CheckCircle, XCircle, Clock,
  ShieldCheck, RefreshCw, FileBadge, AlertCircle,
} from 'lucide-react';
import { fspiopCertificationApi } from '../services/api';
import { decisionLabel, hasBeenExercised, finalizeBlocker, verdictSummary } from '../utils/certification';
import { useAuth } from '../contexts/AuthContext';

const STATUS_STYLE = {
  PASS:             { bg: 'bg-green-100', tx: 'text-green-800', label: 'PASS' },
  CONDITIONAL_PASS: { bg: 'bg-amber-100', tx: 'text-amber-800', label: 'CONDITIONAL PASS' },
  FAIL:             { bg: 'bg-red-100',   tx: 'text-red-800',   label: 'FAIL' },
  INCOMPLETE:       { bg: 'bg-gray-200',  tx: 'text-gray-700',  label: 'INCOMPLETE' },
  NOT_RUN:          { bg: 'bg-gray-100',  tx: 'text-gray-500',  label: 'NOT RUN' },
};

const RESULT_STYLE = {
  PASS:    'bg-green-100 text-green-800',
  CONDITIONAL_PASS: 'bg-amber-100 text-amber-800',
  FAIL:    'bg-red-100 text-red-800',
  BLOCKED: 'bg-gray-100 text-gray-500',
  NOT_RUN: 'bg-gray-100 text-gray-500',
};

const RESULT_ICON = {
  PASS:    <CheckCircle className="w-4 h-4 text-green-500" />,
  CONDITIONAL_PASS: <AlertCircle className="w-4 h-4 text-amber-500" />,
  FAIL:    <XCircle className="w-4 h-4 text-red-500" />,
  BLOCKED: <Clock className="w-4 h-4 text-gray-400" />,
  NOT_RUN: <Clock className="w-4 h-4 text-gray-300" />,
};

/** A run in flight polls this often; it finishes in well under a minute. */
const POLL_MS = 2000;

export default function Certification() {
  const { user } = useAuth();
  const mayAutoRun = Boolean(user?.autoCertEnabled);

  const [progress, setProgress]   = useState(null);
  const [report, setReport]       = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [starting, setStarting]   = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError]         = useState(null);
  const [notice, setNotice]       = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [prog, latest] = await Promise.all([
        fspiopCertificationApi.progress().catch(() => null),
        fspiopCertificationApi.latestReport().catch(() => null),
      ]);
      setProgress(prog);
      setReport(latest);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load certification state');
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll while a run is in flight, then refresh the report it produced.
  const pollRun = useCallback(async () => {
    try {
      const s = await fspiopCertificationApi.autoRunStatus();
      setRunStatus(s);
      if (s.state !== 'RUNNING' && s.state !== 'FINALIZING') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        await refresh();
      }
    } catch {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [refresh]);

  useEffect(() => {
    refresh();
    if (mayAutoRun) {
      // Reattach to a run already in flight — the page may have been reloaded.
      fspiopCertificationApi.autoRunStatus().then((s) => {
        setRunStatus(s);
        if (s.state === 'RUNNING' || s.state === 'FINALIZING') {
          pollRef.current = setInterval(pollRun, POLL_MS);
        }
      }).catch(() => {});
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [refresh, pollRun, mayAutoRun]);

  const startRun = async () => {
    setStarting(true);
    setError(null);
    setNotice(null);
    try {
      const s = await fspiopCertificationApi.startAutoRun();
      setRunStatus(s);
      if (!pollRef.current) pollRef.current = setInterval(pollRun, POLL_MS);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not start the certification run');
    } finally {
      setStarting(false);
    }
  };

  const download = async (fn, failure) => {
    try {
      const { blob, filename } = await fn();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice(e?.response?.status === 409 ? failure
        : (e?.response?.data?.error || 'Download failed'));
    }
  };

  const running = runStatus && (runStatus.state === 'RUNNING' || runStatus.state === 'FINALIZING');
  // The run in progress once anything in it has been scored — after a rejection
  // that is the run that matters — otherwise the last issued report.
  const showingLive = hasBeenExercised(progress) || !report;
  const shown   = showingLive ? progress : report;
  const blocker = finalizeBlocker(progress);

  const finalize = async () => {
    if (!window.confirm(`Finalize and issue the signed certification report?\n\n${verdictSummary(progress)}.\n\n`
      + 'The run closes, its report goes to an AfricaNenda authorising officer, and new traffic starts a new run.')) return;
    setFinalizing(true); setError(null); setNotice(null);
    try {
      await fspiopCertificationApi.finalize();
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not finalize the run');
    } finally {
      setFinalizing(false);
    }
  };
  const status  = STATUS_STYLE[shown?.status] || STATUS_STYLE.NOT_RUN;
  const score   = shown?.scorecard || {};
  // The officer's decision on the finalized report, as the ISO 20022 page shows it.
  // The certificate exists only once it is approved.
  const signOff  = report?.signOff;
  const approved = Boolean(signOff && signOff.decision?.startsWith('APPROVED'));
  const rejected = signOff?.decision === 'REJECTED';
  // A revoked certificate no longer stands; its reason is the note on the decision.
  const revoked  = signOff?.decision === 'REVOKED';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading certification…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-primary-600" /> FSPIOP Certification
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Your run against the AfricaNenda FSPIOP test collection. The certificate is
            issued once an AfricaNenda authorising officer approves a finalized passing run.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="btn btn-secondary" disabled={running || finalizing}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </button>
          {mayAutoRun && (
            <button onClick={startRun} className="btn btn-primary" disabled={running || starting}>
              {running || starting
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Running…</>
                : <><Play className="w-4 h-4 mr-1" /> Run all test scenarios</>}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="card border-red-200 bg-red-50 text-red-800 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}
      {notice && (
        <div className="card border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
          <Clock className="w-4 h-4 mt-0.5" /> {notice}
        </div>
      )}

      {/* Live run progress */}
      {runStatus && runStatus.state !== 'IDLE' && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary-600" /> Automated run
            </h2>
            <span className="text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-700">
              {runStatus.state}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-3">{runStatus.message}</p>
          {runStatus.totalCases > 0 && (
            <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.round((runStatus.casesIssued / runStatus.totalCases) * 100)}%` }}
              />
            </div>
          )}
          {(runStatus.outcomes || []).length > 0 && (
            <div className="text-xs font-mono space-y-1 max-h-52 overflow-y-auto">
              {runStatus.outcomes.map((o) => (
                <div key={o.caseId} className="flex items-start gap-2">
                  <span className="text-gray-500 w-24 shrink-0">{o.caseId}</span>
                  <span className={o.execution === 'ERROR' ? 'text-red-600' : 'text-gray-700'}>
                    {o.detail || o.execution}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scorecard */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-gray-900">
            {showingLive ? 'Active run' : 'Latest finalized report'}
          </h2>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded ${status.bg} ${status.tx}`}>
            {status.label}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Stat label="Mandatory passed"
                value={`${score.mandatory?.passed ?? 0} / ${score.mandatory?.total ?? 0}`} />
          <Stat label="Mandatory failed" value={score.mandatory?.failed ?? 0} />
          <Stat label="Optional passed"
                value={`${score.optional?.passed ?? 0} / ${score.optional?.total ?? 0}`} />
          <Stat label="Pass rate"
                value={score.overallPassRate != null
                  ? `${Math.round(score.overallPassRate * 100)}%` : '—'} />
        </div>

        {showingLive && hasBeenExercised(progress) && (
          <div className="mb-4 border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-gray-700 flex-1 min-w-[16rem]">
              {blocker
                ? <><span className="font-medium">Not ready to finalize.</span> {blocker}</>
                : <>Ready to finalize: {verdictSummary(progress)}.</>}
            </p>
            <button onClick={finalize} disabled={Boolean(blocker) || finalizing || running}
                    title={blocker || undefined}
                    className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {finalizing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileBadge className="w-4 h-4 mr-1" />}
              Finalize &amp; issue
            </button>
          </div>
        )}

        {showingLive && report && (
          <p className="mb-4 text-xs text-gray-500">
            Your last finalized report, {report.reportId}:{' '}
            {report.signOff ? decisionLabel(report.signOff.decision).toLowerCase() : 'awaiting an AfricaNenda authorising officer'}.
          </p>
        )}

        {report && !showingLive && (
          <div className={`mb-4 border rounded-lg p-3 flex items-start gap-2 text-sm ${
            approved ? 'bg-green-50 border-green-200 text-green-900'
              : rejected || revoked ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}>
            <FileBadge className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <p>
              {signOff
                ? <>
                    {decisionLabel(signOff.decision)} by {signOff.authorisingOfficer}
                    {signOff.title ? ` (${signOff.title})` : ''}
                    {signOff.decisionAt ? ` on ${signOff.decisionAt.slice(0, 10)}` : ''}
                    {signOff.conditions?.length > 0 && <> — {signOff.conditions[0].condition}</>}
                    {revoked && <>. Finalize a new run to be certified again.</>}
                    {report.reportId && <span className="text-xs opacity-75"> · report {report.reportId}</span>}
                  </>
                : <>
                    Awaiting an AfricaNenda authorising officer’s sign-off. The certificate is issued once the report
                    is approved.
                    {report.reportId && <span className="text-xs opacity-75"> · report {report.reportId}</span>}
                  </>}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button className="btn btn-secondary"
                  onClick={() => download(fspiopCertificationApi.downloadInstructions,
                                          'Instruction pack unavailable')}>
            <Download className="w-4 h-4 mr-1" /> Certification pack
          </button>
          <button className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!approved}
                  title={approved ? undefined
                    : revoked ? 'The certificate for this report was revoked'
                    : 'Available once AfricaNenda approves your finalized report'}
                  onClick={() => download(fspiopCertificationApi.downloadCertificate,
                    'No approved certification yet — a finalized passing run is awaiting an AfricaNenda authorising officer’s sign-off.')}>
            <FileBadge className="w-4 h-4 mr-1" /> Certificate (PDF)
          </button>
        </div>
      </div>

      {/* Per-category results */}
      {(shown?.categories || []).length > 0 && (
        <div className="card">
          <h2 className="font-medium text-gray-900 mb-3">Categories</h2>
          <div className="divide-y divide-gray-100">
            {shown.categories.map((cat) => {
              const result = typeof cat.result === 'string' ? cat.result : cat.result?.status;
              return (
                <div key={cat.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2">
                    {RESULT_ICON[result] || RESULT_ICON.NOT_RUN}
                    <span className="text-sm text-gray-800">{cat.name}</span>
                    <span className="text-xs text-gray-400">{cat.id}</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${RESULT_STYLE[result] || RESULT_STYLE.NOT_RUN}`}>
                    {(result || 'NOT_RUN').replace(/_/g, ' ')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 font-mono">{value}</div>
    </div>
  );
}
